import React, { useState } from 'react';
import { setWeeklyGoal } from '../api/placement';
// CSS lives in PlacementQuiz.css (imported by PlacementQuiz / dashboard)
// Re-import here so the modal works when rendered standalone from the dashboard.
import './PlacementQuiz.css';

const GOAL_OPTIONS = [
  { minutes: 10, label: '10 min', desc: 'Light touch' },
  { minutes: 15, label: '15 min', desc: 'Recommended' },
  { minutes: 20, label: '20 min', desc: 'Solid build' },
  { minutes: 30, label: '30 min', desc: 'Power mode' },
];

export interface WeeklyGoalPickerProps {
  studentId: string;
  /** Called after the goal is saved successfully */
  onSaved: (minutes: number) => void;
  onCancel: () => void;
}

const WeeklyGoalPicker: React.FC<WeeklyGoalPickerProps> = ({
  studentId,
  onSaved,
  onCancel,
}) => {
  const [selected, setSelected] = useState<number | null>(15);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSave = async () => {
    if (selected == null) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await setWeeklyGoal(studentId, selected);
      onSaved(selected);
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.error || err?.message || 'Failed to save goal. Please try again.'
      );
      setSaving(false);
    }
  };

  // Close on overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div className="cvr-portal pq-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="wgp-title" onClick={handleOverlayClick}>
      <div className="pq-modal">
        <div className="pq-modal__head">
          <h2 className="pq-modal__title" id="wgp-title">Pick your weekly goal</h2>
          <button
            className="pq-modal__close"
            type="button"
            aria-label="Close"
            onClick={onCancel}
            disabled={saving}
          >
            &#215;
          </button>
        </div>
        <p className="pq-modal__sub">
          How many minutes a day do you want to practise? You can change this any time.
        </p>

        <div className="pq-modal__options" role="radiogroup" aria-label="Daily goal options">
          {GOAL_OPTIONS.map((opt) => (
            <button
              key={opt.minutes}
              type="button"
              role="radio"
              aria-checked={selected === opt.minutes}
              className={`pq-modal__option${selected === opt.minutes ? ' is-selected' : ''}`}
              onClick={() => setSelected(opt.minutes)}
              disabled={saving}
            >
              <span className="pq-modal__option__val">{opt.label}</span>
              <span className="pq-modal__option__unit">{opt.desc}</span>
            </button>
          ))}
        </div>

        {errorMsg && (
          <div className="pq-error" style={{ marginBottom: 14 }}>{errorMsg}</div>
        )}

        <button
          className="pq-modal__cta"
          type="button"
          onClick={handleSave}
          disabled={saving || selected == null}
        >
          {saving ? 'Saving…' : 'Set my goal'}
        </button>
      </div>
    </div>
  );
};

export default WeeklyGoalPicker;
